import { last, partition, sum, zip } from 'lodash';
import {
  Direction,
  HorizontalResizable,
  Resizable,
  ResizableHorizontalChildren,
  State,
  VerticalResizable,
} from './types';
import { RESIZER_ATTRIBUTE } from './consts';

export const updateConfigs = (
  state: State,
  containerElement: HTMLDivElement | null,
  getValueFromDOMRect: (rect: DOMRect) => number
) => {
  if (!containerElement) return state;
  const { boxes, containerBox } = getContainerState(containerElement);
  const configs = updateConfigsFromBoxes({
    configs: state.configs,
    boxes,
    containerBox,
    getValueFromDOMRect,
    isStyleInitialized: state.isStyleInitialized,
  });
  return {
    ...state,
    startState: { ...state.startState, boxes, containerBox },
    configs,
  };
};

type UpdateConfigsFromBoxesArgs = {
  configs: State['configs'];
  boxes: DOMRect[];
  containerBox: DOMRect;
  getValueFromDOMRect: (rect: DOMRect) => number;
  isStyleInitialized: boolean;
};

export const updateConfigsFromBoxes = ({
  configs,
  boxes,
  containerBox,
  getValueFromDOMRect,
  isStyleInitialized,
}: UpdateConfigsFromBoxesArgs) =>
  configs.map((config, index) => {
    const value = getCurrentValue({
      configs,
      boxes,
      containerBox,
      getValueFromDOMRect,
      isStyleInitialized,
      config,
      index,
    });
    return {
      ...config,
      currentValue: value,
      startValue: value,
    };
  });

type GetCurrentValueArgs = {
  configs: State['configs'];
  boxes: DOMRect[];
  containerBox: DOMRect;
  getValueFromDOMRect: (rect: DOMRect) => number;
  isStyleInitialized: boolean;
  config: Resizable;
  index: number;
};

const getCurrentValue = ({
  configs,
  boxes,
  containerBox,
  getValueFromDOMRect,
  isStyleInitialized,
  config: { isCollapsed, shouldPreserveValue, currentValue, initialValue },
  index,
}: GetCurrentValueArgs) => {
  if (!isCollapsed) {
    return getValueFromDOMRect(boxes[index]);
  }

  if (shouldPreserveValue) {
    if (isStyleInitialized) return currentValue;
    if (isNumber(initialValue)) return initialValue;
  }

  // Defaulting to the average size of resizable box.
  return getValueFromDOMRect(containerBox) / configs.length;
};

const isNumber = (value: any): value is number => Number.isFinite(value);

export const getContainerState = (containerElement: HTMLDivElement) => {
  const [resizers, sections] = partition(
    Array.from(containerElement.children),
    child => child.hasAttribute(RESIZER_ATTRIBUTE)
  );
  const boxes = sections.map(child => child.getBoundingClientRect());

  return {
    resizers,
    boxes,
    containerBox: containerElement.getBoundingClientRect(),
  };
};

export const consumeDeltaSubtract = (
  delta: number,
  configIndex: number,
  state: State
): [State, number] => {
  const config = state.configs[configIndex];
  if (!config) return [state, 0];
  const direction = delta >= 0 ? 1 : -1;
  if (config.isCollapsed) {
    const [newState, propagatedDelta] = consumeDeltaSubtract(
      delta,
      configIndex + direction,
      state
    );
    return [newState, propagatedDelta];
  }

  const maxConsumableDelta = direction * (config.startValue - config.minValue);
  if (direction * maxConsumableDelta >= direction * delta) {
    const newValue = config.startValue - direction * delta;
    return [updateStateWithNewValue(state, configIndex, newValue), delta];
  }
  const newValue = config.startValue - direction * maxConsumableDelta;
  const [newState, propagatedDelta] = state.isPropagate
    ? consumeDeltaSubtract(
        delta - maxConsumableDelta,
        configIndex + direction,
        state
      )
    : [state, direction * maxConsumableDelta];
  return [
    updateStateWithNewValue(newState, configIndex, newValue),
    maxConsumableDelta + propagatedDelta,
  ];
};

export const consumeDeltaAdd = (
  delta: number,
  configIndex: number,
  state: State
): [State, number] => {
  const config = state.configs[configIndex];
  if (!config) return [state, 0];
  const direction = delta >= 0 ? 1 : -1; // delta positive
  if (config.isCollapsed) {
    const [newState, propagatedDelta] = consumeDeltaAdd(
      delta,
      configIndex + direction,
      state
    );
    return [newState, propagatedDelta];
  }

  const maxConsumableDelta = direction * (config.maxValue - config.startValue);
  if (direction * maxConsumableDelta >= direction * delta) {
    const newValue = config.startValue + direction * delta;
    return [updateStateWithNewValue(state, configIndex, newValue), delta];
  }
  const newValue = config.startValue - direction * maxConsumableDelta;
  const [newState, propagatedDelta] = state.isPropagate
    ? consumeDeltaAdd(
        delta - maxConsumableDelta,
        configIndex + direction,
        state
      )
    : [state, direction * maxConsumableDelta];
  return [
    updateStateWithNewValue(newState, configIndex, newValue),
    maxConsumableDelta + propagatedDelta,
  ];
};

export const updateStateWithNewValue = (
  state: State,
  configIndex: number,
  currentValue: number
) => {
  const config = state.configs[configIndex];
  if (!config) return state;
  return {
    ...state,
    configs: state.configs.map((config, index) =>
      index === configIndex ? { ...config, currentValue } : config
    ),
  };
};

const isResizerDisabled = (
  acc: boolean[],
  { isCollapsed }: State['configs'][0]
) => [...acc, (last(acc) ?? true) && isCollapsed];

export const setStyles = (state: State) => {
  const prop = state.direction === Direction.HORIZONTAL ? 'width' : 'height';
  const minProp =
    state.direction === Direction.HORIZONTAL ? 'minWidth' : 'minHeight';

  const disabledResizersFromStart = state.configs.reduce(isResizerDisabled, []);
  const disabledResizersFromEnd = Array.from(state.configs)
    .reverse()
    .reduce(isResizerDisabled, [])
    .reverse();
  const disabledResizers = zip(
    disabledResizersFromStart.slice(0, -1),
    disabledResizersFromEnd.slice(1)
  ).map(([a, b]) => a || b);

  return {
    ...state,
    styles: state.configs.map(
      ({ currentValue, minValue, isCollapsed, collapsedValue }) =>
        isCollapsed
          ? {
              flex: 'none',
              [prop]: collapsedValue,
            }
          : {
              flex: 'auto',
              [prop]: currentValue,
              [minProp]: minValue,
            }
    ),
    resizerStyles: disabledResizers.map(isDisabled =>
      isDisabled ? { display: 'none' } : {}
    ),
    isStyleInitialized: true,
  };
};

export const getNewValue = (
  { startState: { containerBox }, configs, direction }: State,
  configIndex: number
) => {
  const value =
    direction === Direction.HORIZONTAL
      ? containerBox.width
      : containerBox.height;

  return (
    value -
    sum(
      configs
        .filter((_, index) => index !== configIndex)
        .map(({ currentValue, isCollapsed, collapsedValue }) =>
          isCollapsed ? collapsedValue : currentValue
        )
    )
  );
};

export const getHorizontalConfigFromChild = ({
  props: {
    isCollapsed = false,
    minWidth = 0,
    maxWidth = Infinity,
    collapsedWidth = 0,
    initialWidth,
    shouldPreserveValue = false,
  },
}: React.ReactElement<Record<string, any> & HorizontalResizable>) => ({
  isCollapsed,
  minValue: minWidth,
  maxValue: maxWidth,
  initialValue: initialWidth,
  collapsedValue: collapsedWidth,
  startValue: 0,
  currentValue: 0,
  shouldPreserveValue,
});

export const getVerticalConfigFromChild = ({
  props: {
    isCollapsed = false,
    minHeight = 0,
    maxHeight = Infinity,
    collapsedHeight = 0,
    initialHeight,
    shouldPreserveValue = false,
  },
}: React.ReactElement<Record<string, any> & VerticalResizable>) => ({
  isCollapsed,
  minValue: minHeight,
  maxValue: maxHeight,
  initialValue: initialHeight,
  collapsedValue: collapsedHeight,
  startValue: 0,
  currentValue: 0,
  shouldPreserveValue,
});

/**
 * Does not compare ALL children props, only the ones currently needed
 */
export const haveChildrenDimensionsConfigPropsChangedHorizontal = (
  children: ResizableHorizontalChildren,
  internalConfigsState: Resizable[]
) =>
  children
    .map((child, index) => ({
      currentConfig: internalConfigsState[index],
      nextConfig: getHorizontalConfigFromChild(child),
    }))
    .some(
      ({ currentConfig, nextConfig }) =>
        !isEqualDimensionConfig(currentConfig, nextConfig)
    );

export const haveChildrenDimensionsConfigPropsChangedVertical = (
  children: ResizableHorizontalChildren,
  internalConfigsState: Resizable[]
) =>
  children
    .map((child, index) => ({
      currentConfig: internalConfigsState[index],
      nextConfig: getVerticalConfigFromChild(child),
    }))
    .some(
      ({ currentConfig, nextConfig }) =>
        !isEqualDimensionConfig(currentConfig, nextConfig)
    );

/**
 * Does not compare ALL children props, only the ones currently needed
 */
const isEqualDimensionConfig = (
  currentConfig: Resizable,
  nextConfig: Resizable
) =>
  (['minValue', 'maxValue', 'initialValue'] as (keyof Resizable)[]).every(
    key => currentConfig[key] === nextConfig[key]
  );

export const toInitialStyle = ({
  props: { isCollapsed, initialWidth },
}: React.ReactElement<Record<string, any> & HorizontalResizable>) =>
  // we use maxWidth because of the flex-grow style. The maxWidth style
  // property is removed with the first resize
  isCollapsed ? { flex: 'none', width: 0 } : { maxWidth: initialWidth };

export const toInitialVerticalStyle = ({
  props: { isCollapsed, collapsedHeight = 0, initialHeight },
}: React.ReactElement<VerticalResizable>): React.CSSProperties => {
  if (isCollapsed) {
    return { flex: 'none', height: collapsedHeight };
  }

  return { maxHeight: initialHeight };
};
